import Button from '@/Components/Button/Button';
import { DialogTitle } from '@/Components/Dialog/Dialog';
import {
    Box,
    Checkbox, CircularProgress,
    FormControlLabel, FormGroup, Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import TableCell from '@mui/material/TableCell';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as API from '../../API';
import type EIDHolder from '../../Models/EIDHolder';
import { Dialog, DialogButtons, DialogContent } from '../Dialog/Dialog';
import { Procedure } from '../Procedure/Procedure';

function EIDButton(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState<boolean>(false);
    const [exists, setExists] = useState<boolean>(false);
    const [patientID, setPatientID] = useState<string>('');
    const [patient, setPatient] = useState<any>({});
    const [eidHolder, setEidHolder] = useState<EIDHolder>();
    const [options, setOptions] = useState<any>({});
    const [orders, setOrders] = useState<any[]>([]);

    const [filterName, setFilterName] = useState(false);
    const [filterCity, setFilterCity] = useState(false);
    const [filterDOB, setFilterDOB] = useState(false);
    const [search, setSearch] = useState('');
    const [foundPatients, setFoundPatients] = useState<any[]>([]);

    useEffect(() => {
        void API.EIDC.getPopupOptions()
            .then((res) => {
                setOptions(res);
            });
    }, []);

    const getOrderForEIDPatient = (patientID: string): void => {
        void API.EIDC.getOrdersForEIDPatient(patientID)
            .then((data) => {
                setPatient({ name: data.Name, dob: data.DOB, pictureURL: data.PictureUrl });
                setOrders(data.Orders);
            });
    };

    const read = (): void => {
        if (loading) {
            return;
        }

        setLoading(true);
        void API.EIDC.checkWCFeID()
            .then((res) => {
                setLoading(false);
                if (res.Error) {
                    return window.alert(t('ErrorReadingWFEID'));
                }
                setOpen(true);
                setExists(res.Exists);
                if (res.Exists) {
                    setPatientID(res.PatientID);
                    getOrderForEIDPatient(res.PatientID);
                }
                else {
                    setEidHolder(res.EIDHolder);
                }
            });
    };

    const makeNewPatient = (): void => {
        setOpen(false);
        void API.EIDC.setSessionFromEIDPopup()
            .then(() => {
                navigate('/Patients/Add');
            });
    };

    const loadPatient = (id?: number): void => {
        setOpen(false);
        navigate('/Patients/Detail/' + (id ?? patientID));
    };

    const newOrder = (): void => {
        setOpen(false);
        navigate(`/Orders/Add/${patientID}`);
    };

    const openOrder = (id: string): void => {
        setOpen(false);
        navigate(`/Orders/Detail/${id}?Edit=true&fromPatientOrEid=true`);
    };

    const findSimilarEIDPatient = (): void => {
        void API.EIDC.getSimilarEIDPatients(filterName, filterCity, filterDOB)
            .then((res) => {
                setFoundPatients(res.Items);
            });
    };

    const findEIDPatient = (): void => {
        if (search === '') {
            return;
        }

        void API.general.findPatientsByTerm(search)
            .then((res) => {
                setFoundPatients(res.Items);
            });
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        findSimilarEIDPatient();
    }, [filterName, filterCity, filterDOB]);

    return (
        <>
            <Button onClick={read} full mediumGreen>{!loading ? t('ReadEIDCard') : <CircularProgress size={15} color="warning" />}</Button>

            { exists
                ? (
                        <Dialog open={open}>
                            <DialogTitle>{t('PatientAlreadyExistsAfterEIDRead')}</DialogTitle>
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid>
                                        <img id="PatientFromEID_Picture" src={'data:image/jpg;base64,' + (patient.pictureURL ?? '/Content/img/empty_profile.gif')} style={{ maxHeight: '100px' }} alt="EID Profile" />
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6">{patient.name} ({patient.dob})</Typography>
                                        { orders?.length
                                            ? (
                                                    <>
                                                        <Typography>{t('EIDOrdersFound1')}</Typography>
                                                        <Typography>{t('EIDOrdersFound2')}</Typography>
                                                    </>
                                                )
                                            : ''}
                                    </Grid>
                                </Grid>
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Time')}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Applicant')}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Procedures')}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Status')}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Urgency')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            { orders?.map((x) => (
                                                <TableRow key={x.ID} onClick={() => openOrder(x.ID)}>
                                                    <TableCell>{x.OrderNumber}</TableCell>
                                                    <TableCell>{moment(x.DateTimeImplementation, 'MM/DD/YYYY hh:mm:ss A').calendar()}</TableCell>
                                                    <TableCell>{x.RequestingPhysician}</TableCell>
                                                    <TableCell>{x.Procedures.map((y: any) => <Procedure key={y.OrderHasProcedure_UID} item={y} />)}</TableCell>
                                                    <TableCell>{x.StatusNumber}: {x.StatusName}</TableCell>
                                                    <TableCell sx={{ backgroundColor: x.UrgencyItem.Color }}>{x.UrgencyItem.Abbreviation}</TableCell>
                                                </TableRow>
                                            ))}
                                            {orders.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ textAlign: 'center' }}>{t('NoOrdersFound')}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </DialogContent>
                            <DialogButtons>
                                <Button mediumGreen onClick={(_) => newOrder()}>{t('NewOrder')}</Button>
                                <Button mediumGreen onClick={(_) => loadPatient()}>{t('LoadPatient')}</Button>
                                <Button onClick={() => setOpen(false)}>{t('Cancel')}</Button>
                            </DialogButtons>
                        </Dialog>
                    )
                : (
                        <Dialog open={open}>
                            <DialogTitle>{t('EIDPatientNotFound')}</DialogTitle>
                            <DialogContent>
                                <Typography variant="h5">{t('FilterSimilarPatientsBy')}</Typography>
                                <Box>
                                    <FormGroup>
                                        <FormControlLabel control={<Checkbox onChange={(e) => setFilterName(e.target.checked)} />} label={t('Name') + ': ' + eidHolder?.Name} />
                                        <FormControlLabel control={<Checkbox onChange={(e) => setFilterCity(e.target.checked)} />} label={t('City') + ': ' + eidHolder?.City} />
                                        <FormControlLabel control={<Checkbox onChange={(e) => setFilterDOB(e.target.checked)} />} label={t('dateOfBirthShort') + ': ' + eidHolder?.DOB} />
                                    </FormGroup>
                                    <TextField fullWidth label={t('FilterInAllPatients')} onChange={(e) => setSearch(e.target.value)} onKeyUp={(e) => e.key === 'Enter' && findEIDPatient()} />
                                </Box>

                                <Typography variant="h5">{t('FilterInAllPatients')}</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{t('Name')}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{t('Address')}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{t('dateOfBirthShort')}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{options.nationalIdLongLabel}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { foundPatients?.map((x) => (
                                            <TableRow key={x.ID} onClick={(_) => loadPatient(x.ID)}>
                                                <TableCell>{x.FullName}</TableCell>
                                                <TableCell>{x.Address}</TableCell>
                                                <TableCell>{x.DateOfBirth}</TableCell>
                                                <TableCell>{x.NationalNumber}</TableCell>
                                            </TableRow>
                                        )) }
                                        { foundPatients && !foundPatients.length && (
                                            <TableRow>
                                                <TableCell colSpan={4} sx={{ textAlign: 'center' }}>{t('NoResultsFound')}</TableCell>
                                            </TableRow>
                                        ) }
                                    </TableBody>
                                </Table>
                            </DialogContent>
                            <DialogButtons>
                                <Button mediumGreen onClick={makeNewPatient}>{t('MakeNewPatient')}</Button>
                                <Button onClick={() => setOpen(false)}>{t('Cancel')}</Button>
                            </DialogButtons>
                        </Dialog>
                    )}
        </>
    );
}

export default EIDButton;
